import * as yup from "yup";
import {
  FormQuestionData,
  EligibilityFormContext_submission_form_layouts_groups as GroupSectionProps,
} from "types/gql-types";
import logger from "services/logger";
import { defaultValidator, typeValidators } from "./components/fields";
import { keyFromIdentity } from "./components/helpers";
import { QuestionProps } from "./components/question";

import { Handle } from "./type";
import { passesForCond } from "./passes-cond";

/** When a child question's `cond` does not apply, skip validation (hidden fields stay `null`). */
const schemaWhenCondInactive = yup.mixed().nullable().optional();

export const passes = ({ cond }: Handle<any>, value: any): boolean =>
  passesForCond(cond ?? null, value);

export const extendWithValidator = (
  validators: Record<string, unknown>,
  question: FormQuestionData,
  questionsMap: Record<string, QuestionProps>,
  parent?: string,
  index?: number,
// eslint-disable-next-line max-params
): Record<string, unknown> => {
  const getQuestionValidator = typeValidators[question.__typename] || defaultValidator;
  const questionValidator = getQuestionValidator(question, questionsMap, index && parent, index);
  if (parent) {
    return Object.entries(questionValidator)
      .reduce((acc, [key, val]) => ({
        ...acc,
        [key]: yup.mixed().when(parent, ([parentVal], _schema) =>
          (passes(question, parentVal) ? val : schemaWhenCondInactive) as yup.AnySchema),
      }), validators);
  }
  return {
    ...validators,
    ...questionValidator,
  };
};

const getQuestion = (
  name: string,
  questionMap: Record<string, QuestionProps>,
): QuestionProps | undefined => {
  const question = questionMap[name];

  if (!question) {
    logger.sendMessage(`Question ${name} not found`);
  }

  return question;
};

export const extendWithValidatorNested = (
  validators: Record<string, unknown>,
  questionIdentity: string[],
  questionsMap: Record<string, QuestionProps>,
  parent?: string,
  index?: number,
// eslint-disable-next-line max-params
): Record<string, unknown> => {
  const questionName = keyFromIdentity(questionIdentity);
  const question = getQuestion(questionName, questionsMap);
  // A reference to a question the form doesn't define must not crash the form (sc-19450).
  if (!question) return validators;
  const { children } = question;
  return children.reduce(
    (acc: any, childIdentity: string[]) =>
      extendWithValidatorNested(acc, childIdentity, questionsMap, questionName, index),
    extendWithValidator(validators, question, questionsMap, parent, index),
  );
};

export const generateSectionValidations = (
  section: GroupSectionProps,
  questionsMap: Record<string, QuestionProps>,
): Record<string, unknown> => {
  const { questions: questionIdentities } = section;
  const sectionValidators = questionIdentities
    .reduce((acc: Record<string, unknown>, identity: string[]) =>
      extendWithValidatorNested(acc, identity, questionsMap), {});
  return sectionValidators;
};
